/**
 * Inline group editor embedded in /homepage slot cards.
 *
 * Lets admin edit the referenced group's mode (auto/manual) + config + see
 * a live preview WITHOUT navigating to /group-contents/:id/edit. Group save
 * is independent of the layout save — each "บันทึก Group" PATCHes the group
 * and invalidates the preview query.
 *
 * UI: always-visible status bar + 2 tabs (แก้ไข / Preview). The Preview
 * tab renders items zipped into the slot's assigned LayoutTemplate (or a
 * flat list fallback when no template is set).
 *
 * Scope (intentionally NOT here — use the full /group-contents form):
 *   - name / slug / type / maxCount editing
 *   - delete
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ExternalLink,
  Megaphone,
  Pencil,
  Plus,
  RefreshCw,
  Save,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type {
  GroupContentConfig,
  GroupContentMode,
  HydratedArticleItem,
  HydratedVideoItem,
  UpdateGroupContentInput,
} from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ConfigEditorArticleAuto } from '@/components/group-contents/config-editor-article-auto';
import { ConfigEditorArticleManual } from '@/components/group-contents/config-editor-article-manual';
import { ConfigEditorVideoAuto } from '@/components/group-contents/config-editor-video-auto';
import { ConfigEditorVideoManual } from '@/components/group-contents/config-editor-video-manual';
import { ConfigEditorUrlManual } from '@/components/group-contents/config-editor-url-manual';
import { ConfigEditorStreamingManual } from '@/components/group-contents/config-editor-streaming-manual';
import { ConfigEditorCustomizedManual } from '@/components/group-contents/config-editor-customized-manual';
import {
  groupContentDetailQueryOptions,
  groupContentPreviewQueryOptions,
  useUpdateGroupContent,
} from '@/lib/group-contents';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import { slotOverridesListQueryOptions } from '@/lib/slot-overrides';
import { LayoutPreview } from './layout-preview';

interface SlotGroupEditorProps {
  /** Group id to edit. */
  groupId: string;
  /** Legacy prop kept for API compatibility with existing call sites; the
   *  underlying LayoutPreview no longer fetches a template (templates were
   *  removed from the model). Accepting null only — non-null is ignored. */
  layoutTemplateId: string | null;
  /** When true, the status bar renders as <h3> + edit button only —
   *  no mode badge, no item count, no dirty marker (live-preview surface
   *  uses this to keep the page reading like a real public render). */
  livePreview?: boolean;
  /** Embedded inside the slot-node-editor — hides the status bar AND the
   *  bundled preview entirely; render JUST the edit modal whose open
   *  state is controlled by the parent (slot's bubble menu trigger).
   *  Slot's own per-item preview lives elsewhere. */
  embedded?: { open: boolean; onOpenChange: (open: boolean) => void };
  /** Context for the PR (ข่าวประชาสัมพันธ์) tab — present when this editor
   *  was opened from a Slot/Carousel-slide preview (e.g. /homepage live
   *  render). When set AND the user has `promos.manage`, the dialog adds
   *  a "PR" tab listing active/upcoming overrides for this exact target
   *  and a "Create PR" button that deep-links to /promos/new with the
   *  target pre-filled. */
  prContext?: {
    pageSlug: string;
    /** Slot or Carousel node id. */
    slotId: string;
    /** null when targeting a Slot, integer when targeting a Carousel slide. */
    slideIndex: number | null;
  };
}

function defaultConfigFor(
  type: 'article' | 'video' | 'url' | 'streaming' | 'customized',
  mode: GroupContentMode,
): GroupContentConfig {
  if (type === 'article' && mode === 'auto')
    return { kind: 'article-auto', categorySlug: null, sort: 'latest' };
  if (type === 'article' && mode === 'manual') return { kind: 'article-manual', items: [] };
  if (type === 'video' && mode === 'auto')
    return { kind: 'video-auto', playlistId: null, sort: 'latest' };
  if (type === 'video' && mode === 'manual') return { kind: 'video-manual', items: [] };
  if (type === 'streaming') return { kind: 'streaming-manual', items: [] };
  if (type === 'customized') return { kind: 'customized-manual', items: [] };
  return { kind: 'url-manual', items: [] };
}

export function SlotGroupEditor({
  groupId,
  layoutTemplateId: _layoutTemplateId,
  livePreview = false,
  embedded,
  prContext,
}: SlotGroupEditorProps) {
  const detailQuery = useQuery(groupContentDetailQueryOptions(groupId));
  const previewQuery = useQuery(groupContentPreviewQueryOptions(groupId));
  const updateMutation = useUpdateGroupContent(groupId);
  const { user } = useSession();
  const canManagePr = hasPermission(user, 'promos.manage');
  const showPrTab = Boolean(prContext && canManagePr);

  // Tab state — only meaningful when PR tab is visible. Defaults to content.
  const [activeTab, setActiveTab] = useState<'content' | 'pr'>('content');

  const group = detailQuery.data ?? null;

  const [internalEditOpen, setInternalEditOpen] = useState(false);
  const editOpen = embedded ? embedded.open : internalEditOpen;
  const setEditOpen = embedded ? embedded.onOpenChange : setInternalEditOpen;

  // Draft buffer for in-progress edits. Reset when the server group changes
  // (after save, switching groups via slot picker, etc.).
  const [draftMode, setDraftMode] = useState<GroupContentMode | null>(null);
  const [draftConfig, setDraftConfig] = useState<GroupContentConfig | null>(null);
  // Stash configs by kind so toggling mode preserves the other side's draft
  // (admin toggle auto → manual → auto should NOT wipe their picks).
  // Cleared when the server group rebases (post-save / switch).
  const [configStash, setConfigStash] = useState<Record<string, GroupContentConfig>>({});
  useEffect(() => {
    if (!group) return;
    setDraftMode(group.mode);
    setDraftConfig(group.config);
    setConfigStash({});
  }, [group]);

  if (detailQuery.isLoading || !group || !draftMode || !draftConfig) {
    return (
      <div className="rounded-md border border-dashed border-slate-200 p-2 text-center text-xs text-slate-400 dark:border-slate-800">
        กำลังโหลด group…
      </div>
    );
  }

  // url + streaming + customized are all manual-only by spec (editor-
  // authored / per-item-typed — no auto source to pull from).
  const canToggleMode =
    group.type !== 'url' && group.type !== 'streaming' && group.type !== 'customized';
  const isDirty =
    draftMode !== group.mode || JSON.stringify(draftConfig) !== JSON.stringify(group.config);
  const previewCount = previewQuery.data?.length ?? 0;

  const handleModeChange = (next: GroupContentMode) => {
    if (next === draftMode) return;
    const nextKind = `${group.type}-${next}` as const;
    // Stash the current side's draft so toggling back restores the picks.
    setConfigStash((prev) => ({ ...prev, [draftConfig.kind]: draftConfig }));
    setDraftMode(next);
    setDraftConfig(configStash[nextKind] ?? defaultConfigFor(group.type, next));
  };

  const handleSave = () => {
    const payload: UpdateGroupContentInput = {
      type: group.type,
      mode: draftMode,
      maxCount: group.maxCount,
      config: draftConfig,
    };
    // Close the modal only on successful save — leave it open on error so
    // the operator can fix and retry without losing context.
    updateMutation.mutate(payload, {
      onSuccess: () => setEditOpen(false),
    });
  };

  const editDialog = (
    <Dialog open={editOpen} onOpenChange={setEditOpen}>
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>
            แก้ Group: {group.name}
            {prContext && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                · <code>{prContext.slotId}</code>
                {prContext.slideIndex !== null && (
                  <span className="ml-1 rounded bg-indigo-100 px-1 text-[10px] text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                    slide #{prContext.slideIndex}
                  </span>
                )}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Tab strip — only shown when PR context + permission. */}
        {showPrTab && prContext && (
          <div className="flex border-b border-slate-200 dark:border-slate-700">
            <TabButton
              active={activeTab === 'content'}
              onClick={() => setActiveTab('content')}
              label="✏️ แก้ Group Content"
            />
            <TabButton
              active={activeTab === 'pr'}
              onClick={() => setActiveTab('pr')}
              label="📣 PR (ข่าวประชาสัมพันธ์)"
            />
          </div>
        )}

        {/* PR tab content */}
        {showPrTab && prContext && activeTab === 'pr' && <PrTabContent context={prContext} />}

        {/* Content tab (default) */}
        {(!showPrTab || activeTab === 'content') && (
          <div className="space-y-3">
            {/* Mode toggle + meta */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Mode</Label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={!canToggleMode}
                    onClick={() => handleModeChange('auto')}
                    className={`rounded-md border px-2.5 py-1 text-xs transition ${
                      draftMode === 'auto'
                        ? 'border-emerald-500 bg-emerald-100 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-900/40 dark:text-emerald-200'
                        : 'border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700'
                    } ${!canToggleMode ? 'cursor-not-allowed opacity-40' : ''}`}
                  >
                    อัตโนมัติ
                  </button>
                  <button
                    type="button"
                    onClick={() => handleModeChange('manual')}
                    className={`rounded-md border px-2.5 py-1 text-xs transition ${
                      draftMode === 'manual'
                        ? 'border-amber-500 bg-amber-100 text-amber-700 dark:border-amber-400 dark:bg-amber-900/40 dark:text-amber-200'
                        : 'border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700'
                    }`}
                  >
                    เลือกเอง
                  </button>
                </div>
                {!canToggleMode && (
                  <span className="text-[10px] text-amber-600">
                    {group.type === 'url'
                      ? 'URL'
                      : group.type === 'streaming'
                        ? 'Streaming'
                        : 'Customized'}{' '}
                    type ใช้ได้แค่ manual
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-400">maxCount: {group.maxCount}</span>
                <Link
                  to="/group-contents/$id/edit"
                  params={{ id: groupId }}
                  className="text-[10px] text-slate-400 underline hover:text-slate-600 dark:hover:text-slate-300"
                >
                  แก้ name / slug / maxCount
                </Link>
              </div>
            </div>

            <div>
              {draftConfig.kind === 'article-auto' && (
                <ConfigEditorArticleAuto config={draftConfig} onChange={setDraftConfig} />
              )}
              {draftConfig.kind === 'article-manual' && (
                <ConfigEditorArticleManual
                  config={draftConfig}
                  maxCount={group.maxCount}
                  onChange={setDraftConfig}
                  previewItems={previewQuery.data?.filter(
                    (it): it is HydratedArticleItem => it.type === 'article',
                  )}
                />
              )}
              {draftConfig.kind === 'video-auto' && (
                <ConfigEditorVideoAuto config={draftConfig} onChange={setDraftConfig} />
              )}
              {draftConfig.kind === 'video-manual' && (
                <ConfigEditorVideoManual
                  config={draftConfig}
                  maxCount={group.maxCount}
                  onChange={setDraftConfig}
                  previewItems={previewQuery.data?.filter(
                    (it): it is HydratedVideoItem => it.type === 'video',
                  )}
                />
              )}
              {draftConfig.kind === 'url-manual' && (
                <ConfigEditorUrlManual
                  config={draftConfig}
                  maxCount={group.maxCount}
                  onChange={setDraftConfig}
                />
              )}
              {draftConfig.kind === 'streaming-manual' && (
                <ConfigEditorStreamingManual
                  config={draftConfig}
                  maxCount={group.maxCount}
                  onChange={setDraftConfig}
                />
              )}
              {draftConfig.kind === 'customized-manual' && (
                <ConfigEditorCustomizedManual
                  config={draftConfig}
                  maxCount={group.maxCount}
                  onChange={setDraftConfig}
                />
              )}
            </div>
          </div>
        )}

        {/* Footer — Save buttons hidden on PR tab (PR has its own actions). */}
        {(!showPrTab || activeTab === 'content') && (
          <DialogFooter className="flex flex-row items-center justify-between gap-2 sm:justify-between">
            <span className="text-[10px] text-slate-400">
              {isDirty ? '● มีการเปลี่ยนแปลงที่ยังไม่บันทึก' : 'ไม่มีการเปลี่ยนแปลง'}
            </span>
            <div className="flex items-center gap-2">
              <DialogClose asChild>
                <Button type="button" variant="outline" size="sm">
                  ปิด
                </Button>
              </DialogClose>
              <Button
                type="button"
                size="sm"
                disabled={!isDirty || updateMutation.isPending}
                onClick={handleSave}
              >
                {updateMutation.isPending ? (
                  <>
                    <RefreshCw className="mr-1 h-3.5 w-3.5 animate-spin" /> กำลังบันทึก
                  </>
                ) : (
                  <>
                    <Save className="mr-1 h-3.5 w-3.5" /> บันทึก Group
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );

  // Embedded mode: render JUST the edit modal. Slot's bubble menu owns
  // the trigger; slot's own per-item preview lives in slot-node-editor.
  if (embedded) {
    return editDialog;
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/40">
      {/* Status bar — compact: name + mode badge + count + edit button +
          dirty marker. live-preview: <h3> name + edit button only. */}
      {livePreview ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-1.5 dark:border-slate-700">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
            {group.name}
          </h3>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <Pencil className="h-3 w-3" />
            แก้ไข
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-1.5 dark:border-slate-700">
          <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
            {group.name}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] ${
              draftMode === 'auto'
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200'
            }`}
          >
            {draftMode === 'auto' ? 'อัตโนมัติ' : 'เลือกเอง'}
          </span>
          <span className="text-[10px] text-slate-400">
            {previewCount} / {group.maxCount} item
          </span>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <Pencil className="h-3 w-3" />
            แก้ไข
          </button>
          {isDirty && <span className="text-[10px] text-amber-600">● ยังไม่บันทึก</span>}
        </div>
      )}

      {/* Always-visible preview below status bar */}
      <div className="p-[3px]">
        <LayoutPreview
          definition={null}
          items={previewQuery.data}
          loading={previewQuery.isLoading}
        />
      </div>

      {/* Edit modal — opens on the status-bar pencil button */}
      {editDialog}
    </div>
  );
}

/* ============================================================ */
/* PR tab helpers                                                  */
/* ============================================================ */

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-xs transition ${
        active
          ? 'border-b-2 border-amber-500 text-amber-700 dark:text-amber-300'
          : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
      }`}
    >
      {label}
    </button>
  );
}

interface PrTabContentProps {
  context: { pageSlug: string; slotId: string; slideIndex: number | null };
}

/** PR list filtered to THIS exact target (page + slot + optional slide). The
 *  list shows status chip + window + source label. "Create" deep-links to
 *  /promos/new with query params; row click goes to /promos/$id/edit. */
function PrTabContent({ context }: PrTabContentProps) {
  // Fetch ALL overrides for the page and filter client-side — list is small
  // (single-page, few rows) and avoids needing a server-side slide_index
  // filter on the list endpoint just for this surface.
  const query = useQuery(slotOverridesListQueryOptions({ pageSlug: context.pageSlug }));
  const rows = (query.data?.items ?? []).filter(
    (r) =>
      r.slotId === context.slotId && r.slideIndex === context.slideIndex && r.status !== 'expired',
  );

  const createSearch = {
    pageSlug: context.pageSlug,
    slotId: context.slotId,
    ...(context.slideIndex !== null ? { slideIndex: context.slideIndex } : {}),
  };

  return (
    <div className="space-y-3 py-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-slate-500">
          PR ที่ตั้งไว้บน{' '}
          {context.slideIndex === null ? (
            <>
              slot <code>{context.slotId}</code>
            </>
          ) : (
            <>
              carousel <code>{context.slotId}</code> slide #{context.slideIndex}
            </>
          )}{' '}
          — หมดเวลาแล้ว PR หายเอง slot กลับเป็นเนื้อหาปกติ
        </p>
        <Link
          to="/promos/new"
          search={createSearch}
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-amber-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-600"
        >
          <Plus className="h-3 w-3" /> สร้าง PR
        </Link>
      </div>

      {query.isLoading && (
        <div className="rounded border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400 dark:border-slate-700">
          กำลังโหลด PR…
        </div>
      )}

      {!query.isLoading && rows.length === 0 && (
        <div className="rounded border border-dashed border-slate-200 p-4 text-center dark:border-slate-700">
          <Megaphone className="mx-auto mb-2 h-5 w-5 text-slate-300" />
          <p className="text-xs text-slate-500">
            ยังไม่มี PR ที่ตั้งไว้บน {context.slideIndex === null ? 'slot' : 'slide'} นี้
          </p>
        </div>
      )}

      {rows.map((row) => {
        const statusClass =
          row.status === 'active'
            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
            : 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300';
        const statusLabel = row.status === 'active' ? 'กำลังแสดง' : 'รอเริ่ม';
        return (
          <Link
            key={row.id}
            to="/promos/$id/edit"
            params={{ id: row.id }}
            className="block rounded-md border border-slate-200 bg-white p-3 transition hover:border-amber-300 hover:bg-amber-50/30 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-amber-700 dark:hover:bg-amber-950/20"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] ${statusClass}`}>
                    {statusLabel}
                  </span>
                  <span className="text-[10px] text-slate-400">
                    {row.source.kind === 'article' ? '📰 บทความ' : '🔗 inline'}
                  </span>
                  {row.isOrphan && (
                    <span className="inline-flex items-center gap-0.5 rounded bg-red-100 px-1 text-[10px] text-red-700 dark:bg-red-900/50 dark:text-red-300">
                      <AlertTriangle className="h-2.5 w-2.5" /> orphan
                    </span>
                  )}
                </div>
                <div className="mt-1 truncate text-sm font-medium text-slate-900 dark:text-slate-50">
                  {row.sourceLabel || '(ไม่มีหัวข้อ)'}
                </div>
                <div className="text-[10px] text-slate-500">
                  {formatRange(row.startsAt, row.endsAt)}
                </div>
              </div>
              <ExternalLink className="h-3 w-3 shrink-0 text-slate-400" />
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function formatRange(startsAt: string, endsAt: string): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' });
  return `${fmt(startsAt)} → ${fmt(endsAt)}`;
}
